import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { TimeReportingFormTypes } from "models/formTypes";
import {
  MonthlyFormContainerProps,
  MonthlyFormHandles,
  MonthTotalReplacement,
} from "models/propTypes/timeReportingPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { DialogEnums, StyleEnums, TimeReportingEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants, TimeReportingConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { FormikProps } from "formik";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import MonthlyForm from "./monthlyForm";
import MonthTotalReplacementList from "../_partial/monthTotalReplacementList";
import { useSalaryPermissions } from "components/salary";
import { useFloatingMessages } from "components/common";
import { useTimeReportingService } from "serviceHooks";
import TimeReportHelperInstance from "components/timeReporting/timeReportHelper";
//#endregion
import * as Yup from "yup";
import { FormHelper, LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import {
  buildMonthTotalsProjectTimesPost,
  getMonthDays,
  getWeekColumnHours,
  MonthTotalIssue,
  MonthTotalPlan,
  periodOverlapsMonth,
  planMonthTotal,
  toCents,
} from "../monthTotals";

type MonthlyRow = TimeReportingFormTypes.MonthlyTimeReportRow;

interface MonthTotalChange {
  row: MonthlyRow;
  plan: MonthTotalPlan;
}

const hasTotal = (row: MonthlyRow) =>
  row.monthTotal !== undefined &&
  row.monthTotal !== null &&
  `${row.monthTotal}` !== "" &&
  !isNaN(Number(row.monthTotal));

const isProjectRow = (row: MonthlyRow) =>
  row.type === TimeReportingEnums.TimeReportType.project && !!row.projectId && row.categoryId !== undefined;

const getRowName = (row: MonthlyRow) => (row.categoryName ? `${row.name} · ${row.categoryName}` : row.name);

const MonthlyFormContainer: ForwardRefRenderFunction<MonthlyFormHandles, MonthlyFormContainerProps> = (props, ref) => {
  const { onFormValidationChange, currentMetaData, currentMonth, dateRange, isEditing } = props;
  const formRef = React.useRef<FormHandles>(null);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { enqueueMessage } = useFloatingMessages();
  const timeReportingService = useTimeReportingService();

  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  React.useImperativeHandle(ref, () => ({
    ...(formRef.current ?? ({} as FormHandles)),
    // Read from the form on request: onEditStateChange reaches the parent through the form's debounce, so a
    // click right after typing would see no changes.
    hasChanges: () => (formRef.current?.getValues?.()?.rows ?? []).some(isChanged),
  }));

  const weeksInMonth = React.useMemo(() => props.weeksInMonth ?? [], [props.weeksInMonth]);

  const sumWeekColumns = (row: MonthlyRow) =>
    weeksInMonth.reduce((cents, week) => cents + toCents(Number((row as any)[week] ?? 0)), 0) / 100;

  const initialValues = React.useMemo<TimeReportingFormTypes.MonthlyTimeReport>(() => {
    const month = currentMonth ?? { rows: [] };
    if (!isEditing) {
      return month;
    }
    return {
      ...month,
      rows: month.rows.map((row) => ({ ...row, monthTotal: sumWeekColumns(row) })),
      headerRow: month.headerRow && { ...month.headerRow, monthTotal: sumWeekColumns(month.headerRow) },
    };
  }, [currentMonth, isEditing]);

  const monthDays = React.useMemo(
    () => (dateRange?.start && dateRange?.end ? getMonthDays(currentMetaData, dateRange.start, dateRange.end) : []),
    [currentMetaData, dateRange]
  );

  const getOriginalRow = (row: MonthlyRow) => initialValues.rows.find((r) => r.uniqueId === row.uniqueId);

  const isChanged = (row: MonthlyRow) => {
    if (!isProjectRow(row) || !hasTotal(row)) {
      return false;
    }
    if (row.isNew) {
      return Number(row.monthTotal) > 0;
    }
    return toCents(Number(row.monthTotal)) !== toCents(Number(getOriginalRow(row)?.monthTotal ?? 0));
  };

  const getPlan = (
    row: MonthlyRow,
    metaData: ApiModel.TimeReportMetaDataWithPeriods = currentMetaData,
    days: ApiModel.TimeReport[] = monthDays
  ) =>
    planMonthTotal(
      metaData,
      days,
      { projectId: row.projectId as string, categoryId: row.categoryId as number },
      Number(row.monthTotal)
    );

  const getChanges = (rows: MonthlyRow[]): MonthTotalChange[] =>
    rows.filter(isChanged).map((row) => ({ row: row, plan: getPlan(row) }));

  const getReplacements = (changes: MonthTotalChange[]): MonthTotalReplacement[] =>
    changes
      .filter(({ plan }) => !plan.issue && plan.replaceableHours > 0)
      .map(({ row, plan }) => ({
        rowName: getRowName(row),
        currentTotal: (toCents(plan.fixedHours) + toCents(plan.replaceableHours)) / 100,
        newTotal: Number(row.monthTotal),
      }));

  const getIssueText = ({ row, plan }: MonthTotalChange) => {
    switch (plan.issue) {
      case MonthTotalIssue.BelowFixedHours:
        return LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReporting.MonthTotalBelowFixedHours,
          "",
          {
            row: getRowName(row),
            hours: `${TimeReportHelperInstance.formatHours(plan.fixedHours)} h`,
          }
        );
      case MonthTotalIssue.NoOpenWorkDays:
        return LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReporting.MonthTotalNoOpenWorkDays,
          "",
          {
            row: getRowName(row),
          }
        );
      case MonthTotalIssue.ExceedsDayLimit:
        return LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReporting.MonthTotalExceedsDayLimit,
          "",
          {
            row: getRowName(row),
            hours: `${TimeReportHelperInstance.formatHours(plan.maxTotal)} h`,
          }
        );
      default:
        return undefined;
    }
  };

  // The week columns preview where a changed total lands; rows that are not changed show what is saved.
  const withPreview = (row: MonthlyRow): MonthlyRow => {
    const original = getOriginalRow(row);
    const previewRow = { ...row } as any;
    weeksInMonth.forEach((week) => {
      previewRow[week] = original ? (original as any)[week] : undefined;
    });
    if (!isChanged(row)) {
      return previewRow;
    }
    const plan = getPlan(row);
    if (plan.issue) {
      return previewRow;
    }
    const weekHours = getWeekColumnHours(plan.dayHours, weeksInMonth);
    weeksInMonth.forEach((week) => {
      previewRow[week] = weekHours[week] > 0 ? weekHours[week] : undefined;
    });
    return previewRow;
  };

  const calculateHeaderRow = (rows: MonthlyRow[], headerRow?: MonthlyRow) => {
    const updatedHeaderRow = { ...headerRow } as any;
    weeksInMonth.forEach((week) => {
      updatedHeaderRow[week] = rows.reduce((cents, row) => cents + toCents(Number((row as any)[week] ?? 0)), 0) / 100;
    });
    updatedHeaderRow.monthTotal = sumWeekColumns(updatedHeaderRow);
    return updatedHeaderRow as MonthlyRow;
  };

  const onMonthTotalChange = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>,
    userInput?: boolean
  ) => {
    const fieldInfo = FormHelper.getTableRowFieldInfo(name);
    if (!userInput || fieldInfo?.rowIndex === undefined) {
      return;
    }
    const { values, setFieldValue, setFieldTouched } = formikProps;
    // Touched straight away, so a total that doesn't fit turns red while typing rather than on blur.
    setFieldTouched(name, true, false);
    const rows = values.rows.map((row, index) =>
      index === fieldInfo.rowIndex ? withPreview({ ...row, monthTotal: updatedValue }) : row
    );
    setFieldValue(FormFieldConstants.TimeReporting.MonthlyTimeReport.Rows, rows);
    setFieldValue(
      FormFieldConstants.TimeReporting.MonthlyTimeReport.HeaderRow,
      calculateHeaderRow(rows, values.headerRow)
    );
  };

  const onCategoryChanged = (
    rows: MonthlyRow[],
    rowIndex: number,
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>
  ) => {
    const previewRows = rows.map((row, index) => (index === rowIndex ? withPreview(row) : row));
    formikProps.setFieldValue(FormFieldConstants.TimeReporting.MonthlyTimeReport.Rows, previewRows);
    formikProps.setFieldValue(
      FormFieldConstants.TimeReporting.MonthlyTimeReport.HeaderRow,
      calculateHeaderRow(previewRows, formikProps.values.headerRow)
    );
  };

  const onRowRemove = (removedRow: MonthlyRow, formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>) => {
    const { values, setFieldValue } = formikProps;
    const remainingRows = values.rows.filter((row) => row.uniqueId !== removedRow.uniqueId);
    setFieldValue(
      FormFieldConstants.TimeReporting.MonthlyTimeReport.HeaderRow,
      calculateHeaderRow(remainingRows, values.headerRow)
    );
  };

  const monthProjects = React.useMemo(() => {
    const { start, end } = dateRange ?? {};
    return start && end
      ? (currentMetaData.projects?.filter((project) =>
          periodOverlapsMonth(start, end, project.startDate, project.endDate)
        ) ?? [])
      : [];
  }, [currentMetaData, dateRange]);

  const getRemainingCategories = (
    project: ApiModel.AvailableProject,
    values: TimeReportingFormTypes.MonthlyTimeReport
  ) => {
    const { start, end } = dateRange ?? {};
    return (
      project.categories?.filter(
        (category) =>
          !!start &&
          !!end &&
          periodOverlapsMonth(start, end, category.startDate, category.endDate) &&
          !values.rows.some((row) => row.projectId === project.id && row.categoryId === category.id)
      ) ?? []
    );
  };

  const getAvailableProjects = async (
    value: string,
    formattedValue: string,
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>
  ): Promise<AutocompleteListItem[]> =>
    monthProjects
      .filter((project) => getRemainingCategories(project, formikProps.values).length > 0)
      .map((project) => ({ id: project.id, value: project.name ?? "", data: project }));

  const getAvailableCategoriesForProject = async (
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>,
    projectId?: string
  ): Promise<AutocompleteListItem[]> => {
    const project = monthProjects.find((p) => p.id === projectId);
    return project
      ? getRemainingCategories(project, formikProps.values).map((c) => ({ id: c.id, value: c.name ?? "", data: c }))
      : [];
  };

  const isCategorySelectionDisabled = (
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>,
    rowIndex?: number
  ) => {
    const row = rowIndex !== undefined ? formikProps.values.rows[rowIndex] : undefined;
    const project = monthProjects.find((p) => p.id === row?.projectId);
    return !row?.isNew || !project || getRemainingCategories(project, formikProps.values).length === 0;
  };

  const onProjectSelect = (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<TimeReportingFormTypes.MonthlyTimeReport>
  ) => {
    const { values, setValues } = formikProps;
    const project = item.data as ApiModel.AvailableProject;
    const categories = getRemainingCategories(project, values);
    if (categories.length === 0) {
      return;
    }
    const category = categories.find((c) => c.isDefault) ?? categories[0];
    const newRow: MonthlyRow = {
      name: item.value,
      type: TimeReportingEnums.TimeReportType.project,
      projectId: item.id?.toString(),
      categoryId: category.id,
      categoryName: category.name,
      isInternal: project.isInternal,
      uniqueId: UtilsHelper.getUniqueId(),
      isNew: true,
    };
    setValues({ ...values, rows: [...values.rows, newRow], projectSuggestions: undefined });
  };

  const saveChanges = async (
    metaData: ApiModel.TimeReportMetaDataWithPeriods,
    monthStart: Date,
    monthEnd: Date,
    changes: MonthTotalChange[]
  ) => {
    const post = buildMonthTotalsProjectTimesPost(
      metaData,
      monthStart,
      monthEnd,
      changes.map(({ row, plan }) => ({
        key: { projectId: row.projectId as string, categoryId: row.categoryId as number },
        plan: plan,
      }))
    );

    await timeReportingService
      .setProjectTimes(post)
      .then(() => props.onMonthSaved?.("saved"))
      .catch((error) => {
        const message =
          error?.status === 403
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.NotEnoughPermissions)
            : error?.message && error.message.length > 0
              ? LocalizationHelperInstance.getFormattedMessage(error.message)
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong);

        if (
          error?.codes &&
          error.codes.length === 1 &&
          error.codes[0] === TimeReportingConstants.AdminWarningForAlteringDeviationsWhenMonthSubmitted
        ) {
          enqueueMessage(message, StyleEnums.DisplayType.Warning);
          props.onMonthSaved?.("savedWithWarning");
        } else {
          enqueueMessage(message, StyleEnums.DisplayType.Danger);
          return Promise.reject(message);
        }
      });
  };

  // The save rewrites every day of the changed rows, so each save is planned against the month as it is right then,
  // not as it was when the page loaded or the dialog opened: time reported on those rows in the meantime is kept.
  const planAgainstCurrentMonth = async (changedRows: MonthlyRow[], monthStart: Date, monthEnd: Date) => {
    const metaData = await timeReportingService
      .filter(currentMetaData.employeeId, monthStart, monthEnd)
      .catch(() => undefined);
    if (!metaData) {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.SomethingWentWrong),
        StyleEnums.DisplayType.Danger
      );
      return undefined;
    }
    const days = getMonthDays(metaData, monthStart, monthEnd);
    const changes = changedRows.map((row) => ({ row: row, plan: getPlan(row, metaData, days) }));

    const issues = changes.map(getIssueText).filter((issue): issue is string => !!issue);
    if (issues.length > 0) {
      enqueueMessage(issues.join("\n"), StyleEnums.DisplayType.Danger);
      return undefined;
    }
    return { metaData: metaData, changes: changes };
  };

  // The parent holds Cancel and month navigation until a save settles; otherwise the save still posts totals
  // the user has just discarded, and its reload can replace the month they moved to with the one they left.
  const whileSaving = async (save: () => Promise<void>) => {
    props.onSavingChange?.(true);
    try {
      await save();
    } finally {
      props.onSavingChange?.(false);
    }
  };

  const handleSubmit = async (values: TimeReportingFormTypes.MonthlyTimeReport) => {
    const changedRows = values.rows.filter(isChanged);
    if (changedRows.length === 0 || !dateRange?.start || !dateRange?.end) {
      props.onMonthSaved?.("unchanged");
      return;
    }
    const { start, end } = dateRange;

    const planned = await planAgainstCurrentMonth(changedRows, start, end);
    if (!planned) {
      return;
    }

    const replacements = getReplacements(planned.changes);
    if (replacements.length === 0) {
      return saveChanges(planned.metaData, start, end, planned.changes);
    }

    const confirmReplace = async () => {
      const replanned = await planAgainstCurrentMonth(changedRows, start, end);
      if (!replanned) {
        return Promise.reject();
      }
      if (JSON.stringify(getReplacements(replanned.changes)) !== JSON.stringify(replacements)) {
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.MonthChangedWhileConfirming),
          StyleEnums.DisplayType.Warning
        );
        // Resolve so the dialog closes: a rejection keeps it open on the stale list, and every retry would compare
        // against that same list and fail. Saving again opens it with the current one.
        return;
      }
      return saveChanges(replanned.metaData, start, end, replanned.changes);
    };

    dispatch(
      displayDialog({
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ReplaceReportedTimeTitle),
        subTitle: LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.TimeReporting.ReplaceReportedTimeDescription
        ),
        titleIcon: IconDefinitionConstants.WWarningCanvas,
        dialogType: DialogEnums.DialogType.Content,
        type: StyleEnums.DisplayType.Warning,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ReplaceAndSave),
            onClick: () => whileSaving(confirmReplace),
          },
        ],
        children: <MonthTotalReplacementList replacements={replacements} />,
        maxWidth: "sm",
        disableCancelWhileSubmitting: true,
      })
    );
  };

  const validationSchema = Yup.object<TimeReportingFormTypes.MonthlyTimeReport>({
    rows: Yup.array().of(
      Yup.object<MonthlyRow>({
        name: Yup.string(),
        monthTotal: Yup.number()
          .min(0)
          .test("monthTotalFitsMonth", "", function (value) {
            const row = { ...(this.parent as MonthlyRow), monthTotal: value };
            if (!isEditing || !isChanged(row)) {
              return true;
            }
            const plan = getPlan(row);
            // A field only shows its error state for a non-empty message.
            return !plan.issue || this.createError({ message: getIssueText({ row: row, plan: plan }) });
          }),
      })
    ),
  });

  const onFormUpdate = (values: TimeReportingFormTypes.MonthlyTimeReport, isValid: boolean) => {
    if (isEditing) {
      const changes = getChanges(values.rows);
      props.onEditStateChange?.({
        rows: values.rows,
        replacements: getReplacements(changes),
        issues: changes.map(getIssueText).filter((issue): issue is string => !!issue),
      });
    }
    props.onFormUpdate?.(values, isValid);
  };

  return (
    <MonthlyForm
      ref={formRef}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={(values: TimeReportingFormTypes.MonthlyTimeReport) => whileSaving(() => handleSubmit(values))}
      tableHeaderValues={initialValues.tableHeader}
      headerRowValues={initialValues.headerRow}
      onFormValidationChange={onFormValidationChange}
      monthLockEntries={props.monthLockEntries}
      onFormUpdate={onFormUpdate}
      salaryDeviationActive={isSalaryDeviationsAvailable}
      weeksInMonth={weeksInMonth}
      dateRange={dateRange}
      employeeId={currentMetaData.employeeId}
      isEditing={isEditing}
      availableProjects={getAvailableProjects}
      availableCategories={getAvailableCategoriesForProject}
      isCategorySelectionDisabled={isCategorySelectionDisabled}
      onProjectSelect={onProjectSelect}
      onMonthTotalChange={onMonthTotalChange}
      onCategoryChanged={onCategoryChanged}
      onRowRemove={onRowRemove}
    />
  );
};

export default forwardRef(MonthlyFormContainer);
